import { allows, limit } from "@giftforward/entitlements";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { SECONDARY_BUTTON_CLASS } from "@/constants/buttons";
import { SCHEDULE_MODE_LABELS } from "@/constants/campaignBuilder";
import { type Recipient, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import {
  applySendScheduleDefaults,
  hasOwnSendSchedule,
  type SendScheduleNotice,
  sendScheduleNoticeLines,
  stripSendSchedule,
} from "@/utils/sendSchedule-helper";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import { AddRecipientModal } from "./AddRecipientModal";
import { BulkUploadModal } from "./BulkUploadModal";
import type { SortField } from "./RecipientsTable";
import { RecipientsTable } from "./RecipientsTable";

function ActionButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        "flex items-center gap-2 px-4 py-2 rounded-btn transition-colors bg-white hover:border-dark-dust hover:bg-clear-dust",
        SECONDARY_BUTTON_CLASS,
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function EmptyState({
  onBulkUpload,
  onAddManually,
}: {
  onBulkUpload: () => void;
  onAddManually: () => void;
}) {
  return (
    <div
      className="m-5 flex flex-col items-center justify-center py-8 gap-6 rounded-lg"
      style={{ border: "1px dashed var(--color-smoke)", background: "var(--color-clear-dust)" }}
    >
      <div className="flex flex-col items-center gap-3">
        <img
          src="https://cdn.shopify.com/s/files/1/1826/6559/files/Recipients_Graphic.png?v=1777057485"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-34 h-34 object-contain"
        />
        <p className="font-lato text-[16px] text-rodeo-black text-center">
          No recipients added yet
        </p>
      </div>
      <div className="flex items-center gap-3">
        <ActionButton
          icon={<GroupAddIcon sx={{ fontSize: 18 }} />}
          label="Bulk Upload"
          onClick={onBulkUpload}
        />
        <ActionButton
          icon={<PersonAddAltIcon sx={{ fontSize: 18 }} />}
          label="Add Manually"
          onClick={onAddManually}
        />
      </div>
    </div>
  );
}

export default function AddRecipients() {
  const {
    recipients,
    setRecipients,
    addRecipient,
    importRecipients,
    openModal,
    closeModal,
    openPlanModal,
    notificationMode,
    scheduleMode,
    scheduleTz,
  } = useCampaignBuilder();
  const { ent } = useMyEntitlements();
  const canSchedulePerRecipient =
    !!ent && allows(ent, "campaign_send", "scheduling", "per_recipient");
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const hasRecipients = recipients.length > 0;

  // null is unlimited. Until the matrix resolves the cap is unknown, so nothing is treated as
  // at-cap — the step's boundary is what keeps controls from acting on an unresolved plan.
  const cap = ent ? limit(ent, "campaign_send", "max_recipients") : null;
  const isRecipientsAtCap = cap !== null && recipients.length >= cap;

  const openModalHandler = () => {
    openModal({
      context: "recipientCapBlocked",
      onPrimary: () => openPlanModal("recipientCapBlocked"),
    });
  };

  const tryOpenBulk = () => {
    if (isRecipientsAtCap) {
      openModalHandler();
    } else {
      setIsBulkOpen(true);
    }
  };

  const tryOpenManual = () => {
    if (isRecipientsAtCap) {
      openModalHandler();
    } else {
      setIsManualOpen(true);
    }
  };

  // Per-recipient dates are read in the campaign's zone, so the defaults are written in it too. One
  // context per operation: the stored dates and the alert's "tomorrow" come from the same clock read.
  const scheduleContext = () => ({
    timeZone: resolveScheduleTimeZone(scheduleTz),
    now: new Date(),
  });

  // GFG-443: every default the import or manual add applied is said out loud, never silent.
  // `then` runs once the sender has read it (the cap warning, after an import over the cap).
  const showSendNotice = (
    notice: SendScheduleNotice | null,
    ctx: ReturnType<typeof scheduleContext>,
    then: () => void = closeModal,
  ) => {
    const noticeLines = notice ? sendScheduleNoticeLines(notice, ctx) : [];
    if (noticeLines.length > 0) {
      openModal({ context: "sendDefaultsApplied", noticeLines, onPrimary: then, onDismiss: then });
    } else {
      then();
    }
  };

  // Unlimited (Enterprise) takes every row; a cap takes what is left under it. Rows keep their send
  // date/time (defaults filled in) only under "Send on different dates"; any other schedule sends
  // everyone together, so a row's own date would only contradict it.
  // The defaults alert comes first, the cap warning after it: the warning's Upgrade leaves for the
  // plan modal, which would otherwise swallow the alert.
  const commitImport = (imported: Recipient[], remaining: number, switchToDifferent = false) => {
    const kept = imported.slice(0, remaining);
    const perRecipient = switchToDifferent || scheduleMode === "different";
    const ctx = scheduleContext();
    const { recipients: rows, notice } = perRecipient
      ? applySendScheduleDefaults(kept, ctx)
      : { recipients: stripSendSchedule(kept), notice: null };
    importRecipients(rows, switchToDifferent ? "different" : undefined);
    const capWarning = () =>
      openModal({
        context: "recipientCapWarning",
        addedCount: kept.length,
        capCount: imported.length - kept.length,
        onPrimary: () => openPlanModal("recipientCapWarning"),
      });
    showSendNotice(notice, ctx, kept.length < imported.length ? capWarning : closeModal);
  };

  const handleImport = (imported: Recipient[]) => {
    const remaining = cap === null ? imported.length : cap - recipients.length;
    if (remaining <= 0) {
      openModal({
        context: "recipientCapBlocked",
        onPrimary: () => openPlanModal("recipientCapBlocked"),
      });
      return;
    }
    // GFG-443: the file has dates but the campaign is set to send everyone together — the sender
    // picks which wins before anything is imported, so no date is applied or dropped silently.
    if (
      notificationMode === "gfg" &&
      scheduleMode !== "different" &&
      canSchedulePerRecipient &&
      hasOwnSendSchedule(imported)
    ) {
      openModal({
        context: "sendDatesConflict",
        dismissible: false,
        scheduleLabel: SCHEDULE_MODE_LABELS[scheduleMode],
        onPrimary: () => commitImport(imported, remaining, true),
        onSecondary: () => commitImport(imported, remaining),
      });
      return;
    }
    commitImport(imported, remaining);
  };

  const handleAddManual = (recipient: Recipient) => {
    if (isRecipientsAtCap) {
      openModalHandler();
      return;
    }
    if (scheduleMode !== "different") {
      addRecipient(recipient);
      return;
    }
    // Same defaults and alert as a bulk import (GFG-443 AC: manual adds follow the same rules).
    const ctx = scheduleContext();
    const { recipients: rows, notice } = applySendScheduleDefaults([recipient], ctx);
    addRecipient(rows[0] ?? recipient);
    showSendNotice(notice, ctx);
  };

  const handleDelete = (ids: Set<string>) => {
    setRecipients(recipients.filter((r) => !ids.has(r.id)));
  };

  const handleEdit = (id: string, field: SortField, value: string) => {
    // Clearing the date clears the row's whole schedule (sent at launch): a time alone is a row the
    // API refuses, and refilling it with tomorrow would make the date impossible to clear.
    const cleared = field === "sendDate" && value === "" ? { sendTime: "" } : {};
    const edited = recipients.map((r) => (r.id === id ? { ...r, [field]: value, ...cleared } : r));
    if (scheduleMode !== "different" || (field !== "sendDate" && field !== "sendTime")) {
      setRecipients(edited);
      return;
    }
    // A send date/time typed into the table follows the import's rules: the missing half gets the
    // default and the alert says so.
    const row = edited.find((r) => r.id === id);
    if (!row) return;
    const ctx = scheduleContext();
    const { recipients: rows, notice } = applySendScheduleDefaults([row], ctx);
    setRecipients(edited.map((r) => (r.id === id ? (rows[0] ?? r) : r)));
    showSendNotice(notice, ctx);
  };

  return (
    <>
      {/* Heading + the Bulk Upload / Add Manually pair no longer sit above the box (they were
          duplicated with the empty state). The heading is now the step's page title (RecipientsStep);
          the add actions live inside the box: the empty state's action group AND, once rows exist,
          the populated list header (RecipientsTable). */}
      <div
        className="rounded-card bg-white mb-5 overflow-hidden"
        style={{ border: "1px solid var(--color-smoke)" }}
      >
        {hasRecipients ? (
          <RecipientsTable
            recipients={recipients}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onBulkUpload={tryOpenBulk}
            onAddManually={tryOpenManual}
          />
        ) : (
          <EmptyState onBulkUpload={tryOpenBulk} onAddManually={tryOpenManual} />
        )}
      </div>

      <BulkUploadModal
        isOpen={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        onImport={handleImport}
      />

      <AddRecipientModal
        isOpen={isManualOpen}
        onClose={() => setIsManualOpen(false)}
        onAdd={handleAddManual}
        existingEmails={new Set(recipients.map((r) => r.email.toLowerCase()))}
      />
    </>
  );
}
